import React, { useEffect, useState } from 'react';
import { DataTable, SectionFeedback, TenantOverviewPanel, formatCommercialRole, formatDateTimeLocal } from './CommercialCommon';

function InvitePanel({ api, user, canManageInvites, tenant, invitations = [], onRefresh }) {
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [feedback, setFeedback] = useState({ error: '', success: '' });
  const [isCreating, setIsCreating] = useState(false);

  if (!tenant || !canManageInvites) return null;

  const handleCreate = async () => {
    setFeedback({ error: '', success: '' });
    setIsCreating(true);
    try {
      await api.post('/commercial/invitations', {
        requesterEmail: user.email,
        inviteeEmail: inviteeEmail.trim().toLowerCase() || undefined,
      });
      setInviteeEmail('');
      setFeedback({ error: '', success: 'Invite code ready.' });
      onRefresh?.();
    } catch (err) {
      setFeedback({ error: err.response?.data?.message || 'Unable to create invitation code.', success: '' });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <section className="commercial-panel commercial-panel--subtle">
      <p className="commercial-panel__eyebrow">Team Access</p>
      <h2 className="commercial-panel__title">Invite Teammates</h2>
      <div className="commercial-form" style={{ marginTop: 16, maxWidth: 560 }}>
        <label className="commercial-form__field"><span>Invitee Email</span><input type="email" value={inviteeEmail} onChange={e => setInviteeEmail(e.target.value)} placeholder="Optional teammate email" /></label>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={handleCreate} disabled={isCreating}>{isCreating ? 'Creating Invite...' : 'Create Invite Code'}</button>
          <span className="commercial-form__hint">Invite codes expire after 7 days.</span>
        </div>
        <SectionFeedback error={feedback.error} success={feedback.success} />
      </div>
      <DataTable columns={[{ key: 'inviteCode', label: 'Code' }, { key: 'inviteeEmail', label: 'Invitee Email', render: row => row.inviteeEmail || 'Available to share' }, { key: 'expiresAt', label: 'Expires' , render: row => formatDateTimeLocal(row.expiresAt) }, { key: 'acceptedAt', label: 'Accepted', render: row => row.acceptedAt ? formatDateTimeLocal(row.acceptedAt) : 'Waiting to be used' }]} rows={invitations} emptyMessage="No invite codes yet." />
    </section>
  );
}

function UserManagementPanel({ api, user, canManageUsers, tenant, onRefresh }) {
  const [users, setUsers] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [currentUserId, setCurrentUserId] = useState('');
  const [feedback, setFeedback] = useState({ error: '', success: '' });
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState('');

  useEffect(() => {
    let isMounted = true;
    const loadUsers = async () => {
      if (!api || !user?.email || !tenant || !canManageUsers) return;
      setLoading(true);
      try {
        const res = await api.get('/commercial/users', { params: { requesterEmail: user.email } });
        if (!isMounted) return;
        setUsers(res.data.users || []);
        setRoleOptions(res.data.roleOptions || []);
        setCurrentUserId(res.data.currentUserId || '');
      } catch (err) {
        if (isMounted) setFeedback({ error: err.response?.data?.message || 'Unable to load company users.', success: '' });
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadUsers();
    return () => { isMounted = false; };
  }, [api, user, tenant, canManageUsers]);

  if (!tenant || !canManageUsers) return null;

  const handleRoleChange = async (targetUserId, role) => {
    setFeedback({ error: '', success: '' });
    setUpdatingId(targetUserId);
    try {
      const res = await api.post('/commercial/users/role', { requesterEmail: user.email, targetUserId, role });
      setUsers(res.data.users || []);
      setFeedback({ error: '', success: 'Role updated.' });
      onRefresh?.();
    } catch (err) {
      setFeedback({ error: err.response?.data?.message || 'Unable to update this user role.', success: '' });
    } finally {
      setUpdatingId('');
    }
  };

  return (
    <section className="commercial-panel">
      <p className="commercial-panel__eyebrow">Admin</p>
      <h2 className="commercial-panel__title">Company Users</h2>
      <SectionFeedback error={feedback.error} success={feedback.success} />
      <div className="commercial-user-table-wrap">
        <table className="commercial-user-table">
          <thead><tr><th>Email</th><th>Current Role</th><th>Change Role</th><th>Status</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="commercial-user-table__empty">Loading company users...</td></tr>
            ) : users.length > 0 ? users.map(tenantUser => {
              const isSelf = tenantUser.id === currentUserId;
              const adminCount = users.filter(candidate => candidate.role === 'Tenant Admin').length;
              const disableDemoteLastAdmin = tenantUser.role === 'Tenant Admin' && adminCount <= 1;
              return (
                <tr key={tenantUser.id}>
                  <td>{tenantUser.email}</td>
                  <td><span className={`commercial-role-pill ${tenantUser.role === 'Tenant Admin' ? 'is-admin' : ''}`}>{formatCommercialRole(tenantUser.role)}</span></td>
                  <td>
                    <select value={tenantUser.role || 'Tenant Member'} disabled={isSelf || updatingId === tenantUser.id || disableDemoteLastAdmin} onChange={e => handleRoleChange(tenantUser.id, e.target.value)}>
                      {roleOptions.map(option => <option key={option} value={option}>{formatCommercialRole(option)}</option>)}
                    </select>
                  </td>
                  <td className="commercial-form__hint">{isSelf ? 'Current user' : disableDemoteLastAdmin ? 'Last company admin' : updatingId === tenantUser.id ? 'Updating...' : ''}</td>
                </tr>
              );
            }) : (
              <tr><td colSpan={4} className="commercial-user-table__empty">No company users found yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function CommercialAdminTab({ api, user, workspace, tenantContext, onRefresh }) {
  return (
    <>
      <TenantOverviewPanel tenant={workspace.tenant} summary={workspace.summary} />
      <UserManagementPanel api={api} user={user} canManageUsers={tenantContext.canManageUsers} tenant={workspace.tenant} onRefresh={onRefresh} />
      <InvitePanel api={api} user={user} canManageInvites={tenantContext.canManageInvites} tenant={workspace.tenant} invitations={workspace.recentInvitations} onRefresh={onRefresh} />
      {(tenantContext.capabilities?.canViewAudit && workspace.recentAuditLogs?.length) ? (
        <section className="commercial-panel">
          <p className="commercial-panel__eyebrow">Activity</p>
          <h2 className="commercial-panel__title">Recent Company Activity</h2>
          <DataTable
            columns={[
              { key: 'createdAt', label: 'Time', render: row => formatDateTimeLocal(row.createdAt) },
              { key: 'summary', label: 'Summary' },
              { key: 'changedByEmail', label: 'Changed By', render: row => row.changedByEmail || 'System' },
            ]}
            rows={workspace.recentAuditLogs || []}
            emptyMessage="No recent activity yet."
          />
        </section>
      ) : null}
    </>
  );
}
